<script setup lang="ts">
import type { HTMLAttributes } from 'vue'

import { useTimeoutFn } from '@vueuse/core'
import { cn } from '~ds/lib/utils'
import { motion } from 'motion-v'
import { computed, ref, watch } from 'vue'

import type { BlinkyAvatarVariants } from '.'

import { blinkyAvatarVariants } from '.'

type Size = NonNullable<BlinkyAvatarVariants['size']>
type Gaze = 'centered' | 'right' | 'down' | 'darting'

const props = withDefaults(defineProps<{
  size?: Size;
  gaze?: Gaze;
  hideEyes?: boolean;
  label?: string;
  class?: HTMLAttributes['class'];
}>(), {
  size: 'md',
  gaze: 'centered',
  hideEyes: false,
})

// Per-size visual maps — ported verbatim from KUIUnifiedBlinky.
const GLOW_SIZE: Record<Size, string> = { 'md': 'size-8', 'lg': 'size-12', 'xl': 'size-32', '2xl': 'size-[11rem]' }
const EYE_GAP: Record<Size, string> = { 'md': 'gap-[5px]', 'lg': 'gap-[7px]', 'xl': 'gap-4', '2xl': 'gap-[24px]' }
const EYE_DIMS: Record<Size, { w: number; h: number }> = {
  'md': { w: 3.152, h: 4.848 },
  'lg': { w: 4.727, h: 7.273 },
  'xl': { w: 12.4, h: 16.5 },
  '2xl': { w: 16, h: 24 },
}
const BLUR: Record<Size, string> = { 'md': '2px', 'lg': '3px', 'xl': '7px', '2xl': '10px' }

// Idle blink — a slow single blink; darting reuses the gaze timeline below.
const DEFAULT_BLINK_ANIMATE = { scaleY: [1, 0.1, 1] }
const DEFAULT_BLINK_TRANSITION = { duration: 0.15, repeat: Number.POSITIVE_INFINITY, repeatDelay: 2 }

const DARTING_DURATION = 3
const DARTING_IDLE_DELAY_MS = 1000
// center → look LEFT → blink → look RIGHT → blink → look UP → center → blink ×2
const DARTING_TIMES = [0, 0.15, 0.19, 0.23, 0.40, 0.44, 0.48, 0.65, 0.78, 0.82, 0.86, 0.90, 1]
const DARTING_GAZE = {
  x: ['0%', '-55%', '-55%', '-55%', '55%', '55%', '55%', '0%', '0%', '0%', '0%', '0%', '0%'],
  y: ['0%', '-10%', '-10%', '-10%', '-10%', '-10%', '-10%', '-50%', '0%', '0%', '0%', '0%', '0%'],
}
const DARTING_BLINK = { scaleY: [1, 1, 0.1, 1, 1, 0.1, 1, 1, 1, 0.1, 1, 0.1, 1] }

const STATIC_GAZE: Record<Exclude<Gaze, 'darting'>, { x: string; y: string }> = {
  right: { x: '30%', y: '-10%' },
  down: { x: '0%', y: '70%' },
  centered: { x: '0%', y: '0%' },
}

const eyeDims = computed(() => EYE_DIMS[props.size])
const blurAmount = computed(() => BLUR[props.size])

// darting runs once, then settles to centered after a short idle delay.
const dartingComplete = ref(props.gaze !== 'darting')

const { start: startIdleDelay, stop: stopIdleDelay } = useTimeoutFn(() => {
  if (props.gaze === 'darting') {
    dartingComplete.value = true
  }
}, DARTING_IDLE_DELAY_MS, { immediate: false })

watch(() => props.gaze, (gaze) => {
  dartingComplete.value = gaze !== 'darting'
  if (gaze !== 'darting') {
    stopIdleDelay()
  }
})

const isDarting = computed(() => props.gaze === 'darting' && !dartingComplete.value)

const gazeAnimate = computed(() => (props.gaze === 'darting' ? DARTING_GAZE : STATIC_GAZE[props.gaze]))
const gazeTransition = computed(() =>
  props.gaze === 'darting'
    ? { duration: DARTING_DURATION, times: DARTING_TIMES, ease: 'easeOut' as const }
    : { duration: 0.3, ease: 'easeOut' as const },
)

const onGazeAnimationComplete = () => {
  if (props.gaze === 'darting') {
    startIdleDelay()
  }
}

const eyeAnimate = computed(() => (isDarting.value ? DARTING_BLINK : DEFAULT_BLINK_ANIMATE))
const eyeTransition = computed(() =>
  isDarting.value
    ? { duration: DARTING_DURATION, times: DARTING_TIMES }
    : DEFAULT_BLINK_TRANSITION,
)
</script>

<template>
  <div
    :class="cn(blinkyAvatarVariants({ size }), props.class)"
    :role="label ? 'img' : 'presentation'"
    :aria-label="label || undefined"
    data-slot="blinky-avatar"
  >
    <div
      :class="cn('absolute rounded-full bg-secondary', GLOW_SIZE[size])"
      :style="{ filter: `blur(${blurAmount})` }"
      aria-hidden="true"
    />
    <motion.div
      v-if="!hideEyes"
      :class="cn('relative flex items-center text-foreground', EYE_GAP[size])"
      :animate="gazeAnimate"
      :transition="gazeTransition"
      :on-animation-complete="onGazeAnimationComplete"
    >
      <motion.div
        v-for="eye in ['left', 'right']"
        :key="eye"
        :animate="eyeAnimate"
        :transition="eyeTransition"
      >
        <svg
          :width="eyeDims.w"
          :height="eyeDims.h"
          :viewBox="`0 0 ${eyeDims.w} ${eyeDims.h}`"
          xmlns="http://www.w3.org/2000/svg"
          overflow="visible"
          aria-hidden="true"
        >
          <ellipse
            :cx="eyeDims.w / 2"
            :cy="eyeDims.h / 2"
            :rx="eyeDims.w / 2"
            :ry="eyeDims.h / 2"
            fill="currentColor"
          />
        </svg>
      </motion.div>
    </motion.div>
  </div>
</template>
